import axios from "../../utils/axios.tsx";
import React, { useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import Link from "@mui/material/Link";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useDispatch, useSelector } from "react-redux";
import { checkEconomicIntegration } from "../../store/economic";
import { RootState, AppDispatch } from "../../store/types";

interface SyncStatus {
  last_sync_start: string;
  last_sync_end: string;
  last_error_traceback: string;
  last_successful_sync_start: string;
  last_successful_sync_end: string;
}

interface SyncStatusProps {
  children?: React.ReactNode;
  content: SyncStatus | string;
  role: string;
}

interface ApiResponse<T> {
  data: T;
}

interface SyncStatusResponse {
  objects: SyncStatus[];
}

// Utility function to convert UTC to local time
const formatDate = (utcString: string) => {
  if (!utcString) return "N/A";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(utcString));
};

function syncIntegration(integration_identifier: string = "economic") {
  // Call /integrations/sync/ with the integration identifer
  // The identifier is hardcoded to economic for now
  // Use axios to make the request
  // Sleep and fake sync call (handy for development):
  // return new Promise((resolve) => {
  //   setTimeout(() => {
  //     resolve();
  //   }, 3000);
  // });

  return axios.post(
    "/v1/integrations/sync/",
    JSON.stringify({ integration_identifier: integration_identifier }),
    { withCredentials: true },
  );
}

function fetchSyncStatus() {
  return axios.get("/v1/integrations/sync_status/");
}

const SyncStatus: React.FC<SyncStatusProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasIntegration: hasEconomicIntegration, isLoading } = useSelector(
    (state: RootState) => state.economic,
  );
  const [awaitingSyncResponse, setAwaitingSyncResponse] = React.useState(false);
  const [syncStatus, setSyncStatus] = React.useState<SyncStatus>(
    typeof props.content === "string"
      ? JSON.parse(props.content)
      : props.content,
  );

  useEffect(() => {
    if (isLoading) {
      dispatch(checkEconomicIntegration());
    }
  }, [dispatch, isLoading]);

  const refetchSyncStatus = () => {
    fetchSyncStatus()
      .then((response: ApiResponse<SyncStatusResponse>) => {
        setSyncStatus(response.data.objects[0]);
      })
      .catch((error: unknown) => {
        console.error("Error fetching sync status:", error);
      });
  };

  const syncNow = () => {
    setAwaitingSyncResponse(true);
    syncIntegration("economic")
      .then(() => {
        refetchSyncStatus();
      })
      .catch((error: unknown) => {
        console.error("Error syncing integration:", error);
      })
      .finally(() => {
        setAwaitingSyncResponse(false);
      });
  };

  return (
    <Box marginBottom={"4%"} marginLeft={"5%"}>
      <Grid container>
        <Grid size={{ xs: 1, md: 1, lg: 1 }}>
          <AvatarPanel />
        </Grid>
        <Grid size={{ xs: 12, md: 10, lg: 6 }}>
          <InputLabel sx={{ marginBottom: "3%", marginTop: "1.5%" }}>
            Integration Status
          </InputLabel>
          {awaitingSyncResponse ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "left",
                alignItems: "center",
                height: "150px",
                marginLeft: "20%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {syncStatus.last_error_traceback ? (
                <>
                  <Typography variant="body1" color="black">
                    {`${i18n._(
                      msg({
                        context:
                          "Response title which is followed by the last sync status info",
                        message: "Last sync:",
                      }),
                    )} ${formatDate(syncStatus.last_sync_end)}`}
                  </Typography>
                  <Typography variant="body1" color="black">
                    {`${i18n._(
                      msg({
                        context:
                          "Response title which is followed by the error while trying to fetch the last sync status info",
                        message: "Error:",
                      }),
                    )} ${syncStatus.last_error_traceback}`}
                    <br />
                    <Trans
                      context={
                        "Explanation of the next steps taken after there is an error with sync."
                      }
                    >
                      An engineer will look into this within the next 4 hours
                      within working hours.
                    </Trans>
                  </Typography>
                  <Typography variant="body1" color="black">
                    {`${i18n._(
                      msg({
                        context:
                          "Response title which is followed by the last successful sync status info",
                        message: "Last successful sync:",
                      }),
                    )} ${formatDate(syncStatus.last_successful_sync_end)}`}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="black">
                  {`${i18n._(
                    msg({
                      context:
                        "Response title which is followed by the last successful sync status info",
                      message: "Last successful sync:",
                    }),
                  )} ${formatDate(syncStatus.last_successful_sync_end)}`}
                </Typography>
              )}
            </>
          )}
          <Button
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "25px",
              whiteSpace: "nowrap",
              fontWeight: "bold",
              fontSize: {
                lg: "0.9rem",
                md: "0.8rem",
                sm: "0.7rem",
                xs: "0.5rem",
              },
              marginBottom: "2%",
              marginTop: "2%",
            }}
            variant="outlined"
            onClick={syncNow}
            loading={awaitingSyncResponse}
          >
            <Trans
              context={"Button title enabling the user to sync the status."}
            >
              Sync Now
            </Trans>
          </Button>
          {!isLoading && !hasEconomicIntegration && (
            <Link
              href="https://secure.e-conomic.com/secure/api1/requestaccess.aspx?appPublicToken=zDuGmuztIyDCRvPmk5heLjPY8rfbYxlc6Tqf1p2MEbU1&redirectUrl=https%3A%2F%2Fasktally.com%2Fintegration%2Fe-conomic%2Fconnect"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Typography variant="body2">
                <Trans
                  context={
                    "Button title enabling the user to connect to 'e-conomic' service."
                  }
                >
                  Click here to connect to e-conomic
                </Trans>
              </Typography>
            </Link>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SyncStatus;
